"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import type { SprigAttributes, SprigPlanGroup } from "./types";

interface User {
  id?: number;
  email?: string | null;
  isStaff?: boolean | null;
  currentAccountId?: number | null;
  roles?: { accountId?: number; role?: string }[];
}

interface Account {
  id?: number;
  planInfo?: { name?: string | null };
  isTrialing?: boolean;
}

interface UseSprigIdentityOptions {
  enabled: boolean;
  user: User | undefined;
  account: Account | undefined;
}

function extractDomain(email: string | null | undefined): string | undefined {
  if (!email) return undefined;
  const parts = email.split("@");
  return parts.length === 2 ? parts[1] : undefined;
}

/**
 * Derives a plan group from the plan name using substring matching.
 * Order matters: checks are prioritized (e.g., "Enterprise Pro" -> "Enterprise").
 * This is intentional for survey segmentation - exact plan names vary but groups are stable.
 */
function derivePlanGroup(planName: string | null | undefined): SprigPlanGroup {
  if (!planName) return "Other";

  const normalizedPlan = planName.toLowerCase();

  if (normalizedPlan.includes("enterprise")) return "Enterprise";
  if (normalizedPlan.includes("team")) return "Team";
  if (normalizedPlan.includes("pro")) return "Pro";
  if (normalizedPlan.includes("free")) return "Free";
  if (normalizedPlan.includes("company")) return "Company";
  if (normalizedPlan.includes("starter")) return "Starter";

  return "Other";
}

function deriveAssetType(pathname: string | undefined): string | undefined {
  if (!pathname) return undefined;

  // Order matters: /assets/folders/[id] could contain any asset type,
  // so we check specific asset paths first before falling back to "Folders".
  if (pathname.includes("/assets/zaps")) return "Zaps";
  if (pathname.includes("/assets/tables")) return "Tables";
  if (pathname.includes("/assets/interfaces")) return "Interfaces";
  if (pathname.includes("/assets/canvases")) return "Canvas";
  if (pathname.includes("/assets/chatbots")) return "Chatbots";
  if (pathname.includes("/assets/connections")) return "Connections";
  if (pathname.includes("/assets/folders")) return "Folders";
  if (pathname.includes("/assets/forms")) return "Forms";

  return undefined;
}

export function useSprigIdentity({
  enabled,
  user,
  account,
}: UseSprigIdentityOptions) {
  const pathname = usePathname();
  // References track navigation state without triggering re-renders.
  // hasSetAttributesReference ensures attributes are sent once per session (Sprig best practice).
  const previousPathReference = useRef<string | undefined>(undefined);
  const hasSetAttributesReference = useRef(false);

  const userId = user?.id;
  const userEmail = user?.email;
  const accountId = account?.id;
  const planName = account?.planInfo?.name;
  const isTrialing = account?.isTrialing;
  const currentUserRole = user?.roles?.find(
    (role) => role.accountId === user?.currentAccountId,
  )?.role;

  useEffect(() => {
    if (!enabled || globalThis.window === undefined || !globalThis.Sprig) {
      return;
    }

    // Wait for plan data to be available to avoid sending stale/undefined plan attributes.
    // Once attributes are set, they persist for the session (hasSetAttributesReference guard).
    if (!userId || !accountId || planName === undefined) {
      return;
    }

    const currentPath: string | undefined = pathname ?? undefined;

    if (hasSetAttributesReference.current) {
      if (currentPath !== previousPathReference.current) {
        globalThis.Sprig("setAttributes", {
          currentPath,
          previousPath: previousPathReference.current,
          asset: deriveAssetType(currentPath),
        });
        previousPathReference.current = currentPath;
      }
      return;
    }

    const attributes: SprigAttributes = {
      userId: String(userId),
      role: currentUserRole,
      locale:
        globalThis.navigator === undefined
          ? undefined
          : globalThis.navigator.language,
      isStaff: user?.isStaff ?? false,
      accountId: String(accountId),
      domain: extractDomain(userEmail),
      isTrialing: isTrialing ?? false,
      plan: planName ?? undefined,
      planGroup: derivePlanGroup(planName),
      currentPath,
      asset: deriveAssetType(currentPath),
    };

    globalThis.Sprig("setAttributes", attributes);
    hasSetAttributesReference.current = true;
    previousPathReference.current = currentPath;
  }, [
    enabled,
    userId,
    userEmail,
    accountId,
    planName,
    isTrialing,
    currentUserRole,
    pathname,
    user?.isStaff,
  ]);

  // Reset references when userId changes to handle account switching.
  // SprigLoader never unmounts (root layout), so this cleanup only runs on re-auth.
  useEffect(() => {
    return () => {
      hasSetAttributesReference.current = false;
      previousPathReference.current = undefined;
    };
  }, [userId]);
}
